import React, { useState, useEffect } from 'react';
import TagDisplay from './TagDisplay';
import SentimentDisplay from './SentimentDisplay';
import EntityTopicDisplay from './EntityTopicDisplay';
import { TranscriptDetail } from './TranscriptDetail';
import { useTeam } from '../contexts/TeamContext';
import type { 
  Transcript, 
  Tag, 
  SentimentData, 
  EmotionData, 
  EntityData, 
  TopicData, 
  RelationshipData 
} from '../types';

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  confidence_score?: number;
  created_by?: string;
}

interface SentimentData {
  overall_sentiment: 'positive' | 'negative' | 'neutral';
  confidence_score: number;
  positive_score: number;
  negative_score: number;
  neutral_score: number;
}

interface EmotionData {
  emotion_type: string;
  intensity: number;
  confidence: number;
  text_evidence?: string;
}

interface EntityData {
  id: string;
  entity_type: 'person' | 'place' | 'organization' | 'date' | 'event' | 'product' | 'concept';
  entity_name: string;
  entity_value: string;
  confidence: number;
  context?: string;
}

interface TopicData {
  id: string;
  topic_name: string;
  topic_category: string;
  relevance_score: number;
  confidence: number;
  keywords: string;
  summary?: string;
  context?: string;
}

interface RelationshipData {
  id: string;
  entity1_name: string;
  entity2_name: string;
  relationship_type: string;
  relationship_strength: number;
  context?: string;
  confidence: number;
}

interface TranscriptListProps {
  onSelectTranscript: (transcript: Transcript) => void;
  selectedTranscriptId?: string;
  refreshTrigger?: number; // To trigger refresh when new transcripts are added
  onTranscriptsLoaded?: (transcripts: Transcript[]) => void; // Callback when transcripts are loaded
  searchQuery?: string; // Search query from parent
}

const TranscriptList: React.FC<TranscriptListProps> = ({ 
  onSelectTranscript, 
  selectedTranscriptId,
  refreshTrigger,
  onTranscriptsLoaded,
  searchQuery
}) => {
  const { currentTeam, isPersonalMode } = useTeam();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcriptTags, setTranscriptTags] = useState<Record<string, Tag[]>>({});
  const [transcriptSentiments, setTranscriptSentiments] = useState<Record<string, { sentiment: SentimentData | null; emotions: EmotionData[] }>>({});
  const [transcriptEntities, setTranscriptEntities] = useState<Record<string, { entities: EntityData[]; topics: TopicData[]; relationships: RelationshipData[] }>>({});
  const [loadingMetadata, setLoadingMetadata] = useState<string | null>(null); // Track which transcript is loading metadata
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });
  const [isSearching, setIsSearching] = useState(false);

  // Display the loaded transcripts (either paginated or search results)
  const displayTranscripts = transcripts;

  const fetchTranscripts = async (isLoadMore = false, searchTerm = '') => {
    try {
      if (!isLoadMore) {
        setLoading(true);
        setTranscripts([]); // Clear existing transcripts when not loading more
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      // When searching, bypass pagination to get all results
      const currentOffset = isSearching && searchTerm ? 0 : (isLoadMore ? pagination.offset + pagination.limit : 0);
      const currentLimit = searchTerm ? 50 : pagination.limit; // Reasonable search limit (50 instead of 1000)
      
      const params = new URLSearchParams({
        limit: currentLimit.toString(),
        offset: currentOffset.toString(),
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      // Add team context if not in personal mode
      if (!isPersonalMode && currentTeam) {
        params.append('teamId', currentTeam.id);
      }
      
      const response = await fetch(`/api/transcripts/list?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          transcripts: Transcript[];
          pagination: {
            total: number;
            limit: number;
            offset: number;
            hasMore: boolean;
          };
          error?: string;
        };
        
        if (data.success) {
          if (isLoadMore && !searchTerm) {
            // Append to existing transcripts
            setTranscripts(prev => [...prev, ...data.transcripts]);
          } else {
            // Replace transcripts (new search or initial load)
            setTranscripts(data.transcripts);
          }
          
          setPagination(data.pagination);
          
          // PERFORMANCE OPTIMIZATION: Removed eager metadata fetching
          // Metadata will now be loaded on-demand when transcript is selected
          
          if (onTranscriptsLoaded && !isLoadMore) {
            onTranscriptsLoaded(data.transcripts);
          }
        } else {
          throw new Error(data.error || 'Failed to load transcripts');
        }
      } else {
        throw new Error(`Failed to load transcripts (Status: ${response.status})`);
      }
    } catch (err) {
      console.error('Error fetching transcripts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transcripts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Handle search from parent component
  useEffect(() => {
    if (searchQuery !== undefined) {
      const isCurrentlySearching = searchQuery.trim().length > 0;
      setIsSearching(isCurrentlySearching);
      
      if (isCurrentlySearching) {
        // Perform server-side search
        fetchTranscripts(false, searchQuery);
      } else if (!isCurrentlySearching && isSearching) {
        // Search was cleared, reload paginated results
        fetchTranscripts(false, '');
      }
    }
  }, [searchQuery]);

  const fetchTagsForTranscripts = async (transcripts: Transcript[], isAppending = false) => {
    try {
      if (!isAppending) {
        // Clear existing tags when not appending
        setTranscriptTags({});
      }
      
      const tagsMap: Record<string, Tag[]> = isAppending ? { ...transcriptTags } : {};
      
      // Fetch tags for each transcript
      await Promise.all(
        transcripts.map(async (transcript) => {
          try {
            const response = await fetch(`/api/tags/list?transcriptId=${transcript.id}`, {
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json() as {
                success: boolean;
                tags?: Tag[];
                error?: string;
              };
              if (data.success) {
                tagsMap[transcript.id] = data.tags || [];
              }
            }
          } catch (error) {
            console.warn(`Failed to fetch tags for transcript ${transcript.id}:`, error);
            tagsMap[transcript.id] = [];
          }
        })
      );

      // Update state with all tags
      setTranscriptTags(tagsMap);
    } catch (error) {
      console.error('Error fetching transcript tags:', error);
    }
  };

  const fetchSentimentsForTranscripts = async (transcripts: Transcript[], isAppending = false) => {
    try {
      if (!isAppending) {
        // Clear existing sentiments when not appending
        setTranscriptSentiments({});
      }
      
      const sentimentsMap: Record<string, { sentiment: SentimentData | null; emotions: EmotionData[] }> = 
        isAppending ? { ...transcriptSentiments } : {};
      
      // Fetch sentiment for each transcript
      await Promise.all(
        transcripts.map(async (transcript) => {
          try {
            const response = await fetch(`/api/sentiment/transcript/${transcript.id}`, {
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json() as {
                success: boolean;
                data?: {
                  sentiment: SentimentData | null;
                  emotions: EmotionData[];
                };
                error?: string;
              };
              if (data.success && data.data) {
                sentimentsMap[transcript.id] = data.data;
              } else {
                sentimentsMap[transcript.id] = { sentiment: null, emotions: [] };
              }
            } else {
              sentimentsMap[transcript.id] = { sentiment: null, emotions: [] };
            }
          } catch (error) {
            console.warn(`Failed to fetch sentiment for transcript ${transcript.id}:`, error);
            sentimentsMap[transcript.id] = { sentiment: null, emotions: [] };
          }
        })
      );

      // Update state with all sentiments
      setTranscriptSentiments(sentimentsMap);
    } catch (error) {
      console.error('Error fetching transcript sentiments:', error);
    }
  };

  const fetchEntitiesForTranscripts = async (transcripts: Transcript[], isAppending = false) => {
    try {
      if (!isAppending) {
        // Clear existing entities when not appending
        setTranscriptEntities({});
      }
      
      const entitiesMap: Record<string, { entities: EntityData[]; topics: TopicData[]; relationships: RelationshipData[] }> = 
        isAppending ? { ...transcriptEntities } : {};
      
      // Fetch entities and topics for each transcript
      await Promise.all(
        transcripts.map(async (transcript) => {
          try {
            const response = await fetch(`/api/entities/transcript/${transcript.id}`, {
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json() as {
                success: boolean;
                data?: {
                  entities: EntityData[];
                  topics: TopicData[];
                  relationships: RelationshipData[];
                };
                error?: string;
              };
              if (data.success && data.data) {
                entitiesMap[transcript.id] = data.data;
              } else {
                entitiesMap[transcript.id] = { entities: [], topics: [], relationships: [] };
              }
            } else {
              entitiesMap[transcript.id] = { entities: [], topics: [], relationships: [] };
            }
          } catch (error) {
            console.warn(`Failed to fetch entities for transcript ${transcript.id}:`, error);
            entitiesMap[transcript.id] = { entities: [], topics: [], relationships: [] };
          }
        })
      );

      // Update state with all entities
      setTranscriptEntities(entitiesMap);
    } catch (error) {
      console.error('Error fetching transcript entities:', error);
    }
  };

  useEffect(() => {
    fetchTranscripts();
  }, []);

  // Refresh when new transcripts are added
  useEffect(() => {
    if (refreshTrigger) {
      fetchTranscripts();
    }
  }, [refreshTrigger]);

  const handleTranscriptClick = (transcript: Transcript) => {
    onSelectTranscript(transcript);
  };

  const handleLoadMore = () => {
    if (!loadingMore && pagination.hasMore && !isSearching) {
      fetchTranscripts(true);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-red-500 text-sm mb-2">{error}</div>
        <button
          onClick={() => fetchTranscripts()}
          className="px-3 py-1 text-sm bg-accent-blue text-white rounded hover:bg-accent-blue-dark"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {/* Transcript List */}
      <div className="flex-1">
        {displayTranscripts.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {isSearching ? (
              <>
                <p className="text-sm">No transcripts found for "{searchQuery}"</p>
                <p className="text-xs mt-1">Try a different search term</p>
              </>
            ) : (
              <>
                <p className="text-sm">No transcripts yet.</p>
                <p className="text-xs mt-1">Start by recording some audio above!</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {displayTranscripts.map((transcript) => (
              <div
                key={transcript.id}
                onClick={() => handleTranscriptClick(transcript)}
                className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 ${
                  selectedTranscriptId === transcript.id
                    ? 'border-accent-purple bg-purple-50'
                    : 'border-gray-200'
                }`}
              >
                <h3 className="font-medium text-sm text-gray-900 truncate mb-1">
                  {transcript.title}
                </h3>
                <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                  {transcript.preview}
                </p>
                
                {/* Compact AI Analysis Row */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {/* Sentiment Display - Inline */}
                  {transcriptSentiments[transcript.id] && transcriptSentiments[transcript.id].sentiment && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      transcriptSentiments[transcript.id].sentiment!.overall_sentiment === 'positive' ? 'bg-green-100 text-green-700' : 
                      transcriptSentiments[transcript.id].sentiment!.overall_sentiment === 'negative' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {transcriptSentiments[transcript.id].sentiment!.overall_sentiment === 'positive' ? '😊' : 
                       transcriptSentiments[transcript.id].sentiment!.overall_sentiment === 'negative' ? '😔' : '😐'}
                    </span>
                  )}
                  
                  {/* Top Tags - Inline */}
                  {transcriptTags[transcript.id] && transcriptTags[transcript.id].length > 0 && (
                    <>
                      {transcriptTags[transcript.id].slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                        >
                          {tag.name}
                        </span>
                      ))}
                      {transcriptTags[transcript.id].length > 2 && (
                        <span className="text-xs text-gray-400">+{transcriptTags[transcript.id].length - 2}</span>
                      )}
                    </>
                  )}

                  {/* Key Entities - Minimal */}
                  {transcriptEntities[transcript.id] && transcriptEntities[transcript.id].entities.length > 0 && (
                    <div className="flex items-center gap-0.5">
                      {transcriptEntities[transcript.id].entities.slice(0, 2).map((entity, index) => (
                        <span
                          key={index}
                          className="text-xs"
                          title={`${entity.entity_type}: ${entity.entity_name}`}
                        >
                          {entity.entity_type === 'person' ? '👤' : 
                           entity.entity_type === 'organization' ? '🏢' : 
                           entity.entity_type === 'place' ? '📍' : '🔖'}
                        </span>
                      ))}
                      {transcriptEntities[transcript.id].entities.length > 2 && (
                        <span className="text-xs text-gray-400">+{transcriptEntities[transcript.id].entities.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Load More Button */}
      {!isSearching && pagination.hasMore && (
        <div className="p-4 border-t border-gray-200 bg-white">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-purple focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingMore ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-accent-purple mr-2"></div>
                Loading more...
              </div>
            ) : (
              `Load More (${pagination.total - displayTranscripts.length} remaining)`
            )}
          </button>
        </div>
      )}
      
      {/* Pagination Info */}
      {!isSearching && displayTranscripts.length > 0 && (
        <div className="p-2 text-xs text-gray-500 text-center bg-gray-50 border-t border-gray-200">
          Showing {displayTranscripts.length} of {pagination.total} transcripts
        </div>
      )}
    </div>
  );
};

export default TranscriptList; 
